/**
 * =============================================================================
 * IDS CRM Web - Check form rules: which questions show, what fails, what is
 * still needed (Workscope forms Phase 1, Gareth 2026-10-22)
 * =============================================================================
 *
 * One reading of a form's rules for every screen that fills or checks a form.
 * The server has the same rules in api/src/utils/formRules.js; both are tested
 * against one table of cases (web/src/utils/formRuleCases.json), so a form the
 * screen lets through is a form the server files as complete.
 *
 * A question can carry:
 *  - show_when { field_key, is: 'answered' | 'any_of' | 'fails' | 'passes', values }
 *    on an earlier question. A hidden question is never needed. Today's
 *    follow_up (one text question opened by one answer) keeps working.
 *  - evidence: 'none' | 'optional' | 'required' | 'on_fail' (photos on the
 *    question). allow_photos: true reads as 'optional'.
 *  - on_fail { note, photo }: what a failed answer demands. The note is the
 *    answer under '<field_key>__note'.
 *  - photo / file questions: required means min_count (at least 1) attachments.
 *
 * A fail is: a Yes/No answer equal to fail_value; a reading outside min / max;
 * a choice (or any of several choices) whose option is marked fails.
 * =============================================================================
 */

export const NOTE_SUFFIX = '__note';
export const ATTACHMENT_STEP_PREFIX = 'template:';

// Workflow fields the form itself does not draw (the van check has its own screen).
const NOT_DRAWN_BY_THE_FORM = new Set(['vehicle_select', 'seat_select']);
const ANSWERLESS = new Set(['instruction', 'photo', 'file']);

export const isEmptyAnswer = (value) => value === undefined || value === null || value === ''
  || (Array.isArray(value) && value.length === 0);

const byOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

/** Live sections, each with its live questions, in form order. */
export function liveSections(schema) {
  return (Array.isArray(schema?.sections) ? schema.sections : [])
    .filter(section => section && !section.archived)
    .slice()
    .sort(byOrder)
    .map(section => ({
      ...section,
      fields: (Array.isArray(section.fields) ? section.fields : [])
        .filter(field => field && !field.archived)
        .slice()
        .sort(byOrder)
    }));
}

/** Every live question by key, follow-ups included. */
export function questionsByKey(schema) {
  const map = new Map();
  liveSections(schema).forEach(section => section.fields.forEach(field => {
    map.set(field.field_key, field);
    if (field.follow_up?.field && !field.follow_up.field.archived) map.set(field.follow_up.field.field_key, field.follow_up.field);
  }));
  return map;
}

/** How many attachments a question has, from a { step_key: [] } map or a flat list with step_key. */
export function attachmentCount(attachments, fieldKey) {
  const stepKey = `${ATTACHMENT_STEP_PREFIX}${fieldKey}`;
  if (Array.isArray(attachments)) return attachments.filter(item => item && item.step_key === stepKey).length;
  if (attachments && typeof attachments === 'object') {
    const list = attachments[stepKey];
    return Array.isArray(list) ? list.length : 0;
  }
  return 0;
}

const numberOf = (value) => {
  if (isEmptyAnswer(value)) return null;
  const parsed = Number(String(value).trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

/** True when this answer counts as a fail for the question. */
export function answerFails(field, answer) {
  if (!field || isEmptyAnswer(answer)) return false;
  switch (field.field_type) {
    case 'yes_no':
    case 'yes_no_na':
      return field.fail_value !== undefined && field.fail_value !== null && field.fail_value !== ''
        && String(answer) === String(field.fail_value);
    case 'reading': {
      const value = numberOf(answer);
      if (value === null) return false;
      const min = numberOf(field.min);
      const max = numberOf(field.max);
      return (min !== null && value < min) || (max !== null && value > max);
    }
    case 'choice':
    case 'multi_choice': {
      const chosen = Array.isArray(answer) ? answer.map(String) : [String(answer)];
      return (field.options || []).some(option => option && option.fails && chosen.includes(String(option.value)));
    }
    default:
      return false;
  }
}

function conditionHolds(condition, parent, answers) {
  const answer = answers?.[parent.field_key];
  switch (condition.is) {
    case 'answered':
      return !isEmptyAnswer(answer);
    case 'fails':
      return answerFails(parent, answer);
    case 'passes':
      return !isEmptyAnswer(answer) && !answerFails(parent, answer);
    case 'any_of': {
      const wanted = (condition.values || []).map(String);
      const given = Array.isArray(answer) ? answer.map(String) : (isEmptyAnswer(answer) ? [] : [String(answer)]);
      return given.some(value => wanted.includes(value));
    }
    default:
      return true;
  }
}

/**
 * Whether a question shows, given the answers so far. A question whose
 * condition question is itself hidden is hidden too. A condition on a question
 * that is not in the form (removed, archived) never hides anything.
 */
export function isQuestionVisible(field, answers, byKey, seen = new Set()) {
  const condition = field?.show_when;
  if (!condition || !condition.field_key) return true;
  const parent = byKey.get(condition.field_key);
  if (!parent || seen.has(field.field_key)) return true;
  seen.add(field.field_key);
  if (!isQuestionVisible(parent, answers, byKey, seen)) return false;
  return conditionHolds(condition, parent, answers);
}

/** Evidence setting, with today's allow_photos read as 'optional'. */
export const evidenceOf = (field) => field?.evidence || (field?.allow_photos ? 'optional' : 'none');

const photosWord = (count) => (count === 1 ? 'a photo' : `${count} photos`);
const filesWord = (count) => (count === 1 ? 'a file' : `${count} files`);

/**
 * Everything still needed before the form is complete, in form order.
 *
 * @param {object} schema
 * @param {object} answers
 * @param {object|Array} attachments step-keyed map (screens) or flat list (saved)
 * @returns {Array<{field_key, message, section, fieldLabel, reason, isFollowUp?, parentFieldKey?}>}
 */
export function outstandingItems(schema, answers, attachments) {
  const given = answers && typeof answers === 'object' ? answers : {};
  const byKey = questionsByKey(schema);
  const items = [];
  const add = (field, section, reason, message, extra = {}) => items.push({
    field_key: field.field_key, message, section: section.title || null, fieldLabel: field.label, reason, ...extra
  });

  const checkQuestion = (field, section, extra = {}) => {
    if (NOT_DRAWN_BY_THE_FORM.has(field.field_type)) return;
    if (!isQuestionVisible(field, given, byKey)) return;
    const answer = given[field.field_key];
    const photos = attachmentCount(attachments, field.field_key);

    if (field.required_on_complete) {
      if (field.field_type === 'photo' || field.field_type === 'file') {
        const needed = Math.max(1, Number(field.min_count) || 1);
        if (photos < needed) {
          const word = field.field_type === 'photo' ? photosWord(needed) : filesWord(needed);
          add(field, section, 'attachments', `${field.label} needs ${word}`, extra);
        }
      } else if (!ANSWERLESS.has(field.field_type) && isEmptyAnswer(answer)) {
        add(field, section, 'required', `${field.label} is required`, extra);
        return;
      }
    }

    const fails = answerFails(field, answer);
    const evidence = evidenceOf(field);
    const photoDemanded = evidence === 'required' || (evidence === 'on_fail' && fails) || (fails && field.on_fail?.photo);
    if (photoDemanded && photos < 1 && field.field_type !== 'photo' && field.field_type !== 'file') {
      add(field, section, 'photo', fails ? `${field.label} failed: add a photo` : `${field.label} needs a photo`, extra);
    }
    if (fails && field.on_fail?.note && isEmptyAnswer(String(given[`${field.field_key}${NOTE_SUFFIX}`] ?? '').trim())) {
      add(field, section, 'note', `${field.label} failed: add a note saying what is wrong`, extra);
    }
  };

  liveSections(schema).forEach(section => {
    section.fields.forEach(field => {
      checkQuestion(field, section);
      const followUp = field.follow_up;
      if (followUp?.field && !followUp.field.archived
        && String(given[field.field_key]) === String(followUp.condition)
        && isQuestionVisible(field, given, byKey)) {
        checkQuestion(followUp.field, section, { isFollowUp: true, parentFieldKey: field.field_key });
      }
    });
  });
  return items;
}
